import React, { useEffect, useMemo, useState } from "react";
import styles from "./Rewards.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import {
  getAppSumoReferrals,
  getAppSumoFeedback,
} from "../../Store/apiStore";
import {
  PeopleIcon,
  CheckCircleIcon,
  ClockIcon,
  HowItWorksBadge,
  G2Logo,
  CapterraLogo,
  PlayLogo,
  AppSumoLogo,
  ProductHuntLogo,
} from "./icons";

/**
 * "Earnings" — every minute this account has earned, feedback and referrals in
 * one list.
 *
 * The two sources are deliberately merged rather than tabbed: a user asking
 * "where did my minutes come from?" wants one answer in date order, not two
 * lists to reconcile. Each entry keeps a badge saying which programme it came
 * from, so the mix stays readable, and the filter narrows to one when they do
 * want to see a single stream.
 *
 * Only earned minutes appear. A claim still in review, or a referral who signed
 * up without paying, is tracked on the screen it belongs to — here it would add
 * a row worth nothing to a list about what was earned.
 *
 * "Verified" is not the same as spendable: feedback minutes land only once
 * every platform is approved, and referral minutes on the 1st. That is what the
 * note at the foot is for.
 */

const PLATFORM_META = {
  appsumo: { Logo: AppSumoLogo, text: "Review the deal on AppSumo" },
  g2: { Logo: G2Logo, text: "Share your feedback on G2" },
  capterra: { Logo: CapterraLogo, text: "Review us on Capterra" },
  play: { Logo: PlayLogo, text: "Leave a review on Google play" },
  producthunt: { Logo: ProductHuntLogo, text: "Review us on Product Hunt" },
};

const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// 15000 -> "15K", 950 -> "950". Long figures would otherwise push the unit and
// the status onto separate lines on a narrow phone.
const fmtMins = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000) {
    const k = v / 1000;
    return `${Number.isInteger(k) ? k : k.toFixed(1)}K`;
  }
  return v.toLocaleString();
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "referral", label: "Referral" },
  { key: "feedback", label: "Feedback" },
];

const Earnings = () => {
  const [referralData, setReferralData] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([getAppSumoReferrals(), getAppSumoFeedback()]).then(
      ([ref, fb]) => {
        if (cancelled) return;
        setReferralData(ref.status === "fulfilled" ? ref.value : null);
        setFeedbackData(fb.status === "fulfilled" ? fb.value : null);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // One shape for both sources, so the list below does not care where a row
  // came from: {id, kind, title, sub, minutes, date, state}.
  const entries = useMemo(() => {
    const out = [];

    // Only what has actually been earned. A submitted-but-unreviewed claim or
    // a referral who has signed up without paying is a hope, not an earning —
    // both are already tracked on the screens they belong to, and listing them
    // here would put rows worth "0 mins" in a history of minutes earned.
    for (const p of feedbackData?.platforms || []) {
      if (p.status !== "approved") continue;
      const meta = PLATFORM_META[p.key] || {};
      out.push({
        id: `fb-${p.key}`,
        kind: "feedback",
        Logo: meta.Logo,
        title: p.name,
        sub: meta.text || "",
        minutes: p.minutes,
        date: p.submittedAt,
      });
    }

    for (const r of referralData?.referrals || []) {
      if (!r.minutesPerMonth) continue;
      out.push({
        id: `ref-${r.userId}`,
        kind: "referral",
        Logo: PeopleIcon,
        title: "Referral purchase",
        sub: `${r.name || r.email || "Someone"} purchased ${r.planName}`,
        minutes: r.minutesPerMonth,
        date: r.firstPaidAt || r.signedUpAt,
      });
    }

    return out;
  }, [feedbackData, referralData]);

  const rows = useMemo(() => {
    const filtered =
      filter === "all" ? entries : entries.filter((e) => e.kind === filter);
    // Undated entries sort last either way rather than jumping to the top on
    // "oldest first", which a bare timestamp comparison would do.
    return [...filtered].sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : null;
      const tb = b.date ? new Date(b.date).getTime() : null;
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return sort === "newest" ? tb - ta : ta - tb;
    });
  }, [entries, filter, sort]);

  return (
    <div className={styles.wrap}>
      <div className={styles.inner}>
        <div className={styles.headerSlot}>
          <HeaderBar title="Earnings" />
        </div>

        <div className={styles.chipRow}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${styles.chip} ${
                filter === f.key ? styles.chipActive : ""
              }`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className={styles.histHead}>
          <h2 className={styles.histTitle}>Earning history</h2>
          <select
            className={styles.histSort}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort earnings"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>

        {loading ? (
          <div className={styles.loading}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className={styles.rowList}>
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <ClockIcon width={32} height={32} />
              </div>
              No earnings yet. Give feedback or refer someone to start earning
              minutes.
            </div>
          </div>
        ) : (
          <div className={styles.taskList}>
            {rows.map((e) => {
              const Logo = e.Logo;
              return (
                <div
                  className={`${styles.taskRow} ${styles.earnRow}`}
                  key={e.id}
                >
                  <span className={styles.taskLogo}>
                    {Logo ? <Logo /> : null}
                  </span>

                  <div className={styles.rowMain}>
                    <span className={styles.rowName}>{e.title}</span>
                    <span className={styles.rowSub}>{e.sub}</span>
                    <span className={styles.earnDate}>{fmtDate(e.date)}</span>
                  </div>

                  <div className={styles.earnRight}>
                    <span className={styles.earnMins}>
                      +{fmtMins(e.minutes)}
                      <span className={styles.earnUnit}>mins</span>
                    </span>
                    <span className={styles.earnVerified}>
                      <CheckCircleIcon width={13} height={13} />
                      Verified
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.howBox} style={{ marginTop: 18 }}>
          <HowItWorksBadge className={styles.earnBadge} />
          <div>
            {/* The two reward types are credited on different rules, so the
                note follows the filter. Referral credits are granted by the
                monthly cron (appsumoReferralCron: "0 0 1 * *") and expire at
                the end of that month — saying so here stops the "where did my
                minutes go?" question. Feedback is verified by hand. On "All"
                both rules apply, so state both. */}
            {filter === "referral" ? (
              <>
                <span className={styles.howFootTitle}>
                  Referral minutes are added on the 1st of every month
                </span>
                <span className={styles.howFootSub}>
                  Valid for that month
                </span>
              </>
            ) : filter === "feedback" ? (
              <>
                <span className={styles.howFootTitle}>
                  Minutes are credited within 24-48 hrs
                </span>
                <span className={styles.howFootSub}>
                  After we verify your feedback
                </span>
              </>
            ) : (
              <>
                <span className={styles.howFootTitle}>
                  Referral minutes are added on the 1st of every month
                </span>
                <span className={styles.howFootSub}>
                  Valid for that month · Feedback minutes are credited within
                  24-48 hrs after we verify them
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Earnings;
